
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export const AUTO_PUBLISH_INTERVALS = {
  'Nå (5 sekunder)': 5,
  'Hvert 15 sekund': 15,
  '30 sekunder': 30,
  '1 minutt': 60,
  '5 minutter': 300,
  'custom': -1
};

interface AutoPublishControlsProps {
  selectedPreset: string;
  customInterval: string;
  showCustomInterval: boolean;
  isAutoPublish?: boolean;
  publishInterval?: number;
  timeUntilNextPublish?: number;
  toggleAutoPublish?: () => void;
  handleIntervalChange: (value: string) => void;
  handleCustomIntervalChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  applyCustomInterval: () => void;
}

export const AutoPublishControls: React.FC<AutoPublishControlsProps> = ({
  selectedPreset,
  customInterval,
  showCustomInterval,
  isAutoPublish = false,
  publishInterval = 5,
  timeUntilNextPublish = 0,
  toggleAutoPublish = () => {},
  handleIntervalChange,
  handleCustomIntervalChange,
  applyCustomInterval
}) => {
  // Use helper function to get interval display
  const getIntervalDisplay = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} sekund${seconds !== 1 ? 'er' : ''}`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      let display = `${minutes} minutt${minutes !== 1 ? 'er' : ''}`;
      if (remainingSeconds > 0) {
        display += ` ${remainingSeconds} sekund${remainingSeconds !== 1 ? 'er' : ''}`;
      }
      return display;
    }
  };

  // Display auto-publish timer if active
  const renderAutoPublishTimer = () => {
    if (!isAutoPublish || timeUntilNextPublish === undefined) return null;
    
    return (
      <div className="text-sm text-gray-600 ml-4">
        Neste publisering om: {timeUntilNextPublish} sekunder
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 mb-4">
      <div className="flex items-center gap-2">
        {toggleAutoPublish && (
          <Button 
            onClick={toggleAutoPublish}
            variant={isAutoPublish ? "destructive" : "default"}
            size="sm"
          >
            {isAutoPublish ? "Stopp Auto-Publisering" : "Start Auto-Publisering"}
          </Button>
        )}
        {renderAutoPublishTimer()}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select
          value={selectedPreset}
          onValueChange={handleIntervalChange}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Velg publiseringsfrekvens" />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(AUTO_PUBLISH_INTERVALS).map((preset) => (
              <SelectItem key={preset} value={preset}>
                {preset}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showCustomInterval && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              placeholder="Sekunder"
              value={customInterval}
              onChange={handleCustomIntervalChange}
              className="w-24"
            />
            <Button variant="outline" size="sm" onClick={applyCustomInterval}>
              Bruk
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
